import { useState, useEffect, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { supabaseSecure, USE_SECURE_COOKIES } from "@/lib/secureClient";
import { clearPersistedListState } from "@/lib/clearListState";

// Use the secure client on production, standard client elsewhere
const client = USE_SECURE_COOKIES ? supabaseSecure : supabase;

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // Track whether the initial session check has completed. We must not
  // expose loading=false until then, otherwise route guards can briefly
  // see user=null and incorrectly redirect away from protected pages.
  const initialCheckDone = useRef(false);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = client.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[useAuth] Auth state changed:', event, session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);
        // Only flip loading off once the initial session check has run.
        // Supabase fires INITIAL_SESSION after getSession resolves; earlier
        // transient events (e.g. an empty session before storage hydration)
        // must not unblock route guards prematurely.
        if (initialCheckDone.current || event === 'INITIAL_SESSION') {
          initialCheckDone.current = true;
          setLoading(false);
        }

        // Update last_sign_in_at when user signs in
        if (event === 'SIGNED_IN' && session?.user?.id) {
          setTimeout(async () => {
            await client
              .from('profiles')
              .update({ last_sign_in_at: new Date().toISOString() })
              .eq('id', session.user.id);
          }, 0);
        }

        // Clear persisted list-state filters on sign out so the next
        // login starts with clean search/filters/pagination
        if (event === 'SIGNED_OUT') {
          clearPersistedListState();
        }
      }
    );

    // THEN check for existing session
    const initSession = async () => {
      try {
        const { data: { session } } = await client.auth.getSession();
        console.log('[useAuth] Initial session check:', session?.user?.email ?? 'no session');
        setSession(session);
        setUser(session?.user ?? null);
      } catch (error) {
        console.error('[useAuth] Error getting session:', error);
      } finally {
        initialCheckDone.current = true;
        setLoading(false);
      }
    };

    initSession();

    return () => subscription.unsubscribe();
  }, []);

  return { user, session, loading };
};
